import { useState, useEffect } from "react";
import { Row, Col, Input, Select, Table } from "antd";
import { useNavigate } from "react-router-dom";
import { fetchBookingsList } from "services/ordersAndBookings.service";
import { renderErrorNotifications } from "helpers/error.helpers";
import {
  getBookingColumns,
  BookingItemExpanded,
  BOOKING_STATUS_OPTIONS,
  BOOKING_FILTER_OPTIONS,
} from "../commons/orders.helpers";

function BookingsTab() {
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("ALL");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  async function load(page, pageSize, status, time) {
    setLoading(true);
    const params = {
      current_page: page,
      page_size: pageSize,
      ...(status && status !== "all" ? { booking_status: status } : {}),
      ...(time && time !== "ALL" ? { filter: time } : {}),
    };
    const { data, errors } = await fetchBookingsList(params);
    setLoading(false);
    if (errors?.length) {
      renderErrorNotifications(errors);
      return;
    }
    setBookings(data?.bookings ?? []);
    const meta = data?.meta?.pagination ?? {};
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize,
      total: meta.total ?? (meta.total_pages ?? 1) * pageSize,
    }));
  }

  useEffect(() => {
    load(1, 10, statusFilter, timeFilter);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusChange = (status) => {
    setStatusFilter(status);
    load(1, pagination.pageSize, status, timeFilter);
  };

  const handleTimeChange = (time) => {
    setTimeFilter(time);
    load(1, pagination.pageSize, statusFilter, time);
  };

  const handleTableChange = (pag) => {
    load(pag.current, pag.pageSize, statusFilter, timeFilter);
  };

  const filtered = bookings.filter((b) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      b.booking_number?.toLowerCase().includes(q) ||
      b.customer_id?.toLowerCase().includes(q) ||
      b.customer_details?.full_name?.toLowerCase().includes(q)
    );
  });

  const columns = getBookingColumns({
    onView: (row) => navigate(`bookings/detail/${row.booking_number}`),
  });

  return (
    <div className="space-y-4 py-4">
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={10} md={7}>
          <Input.Search
            placeholder="Search by booking # or customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={setSearch}
            allowClear
          />
        </Col>
        <Col xs={12} sm={5} md={4}>
          <Select
            value={statusFilter}
            onChange={handleStatusChange}
            options={BOOKING_STATUS_OPTIONS}
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={12} sm={4} md={3}>
          <Select
            value={timeFilter}
            onChange={handleTimeChange}
            options={BOOKING_FILTER_OPTIONS}
            style={{ width: "100%" }}
          />
        </Col>
      </Row>

      <Table
        rowKey="booking_number"
        columns={columns}
        dataSource={filtered}
        loading={loading}
        size="small"
        scroll={{ x: 800 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: false,
          position: ["bottomRight"],
        }}
        onChange={handleTableChange}
        expandable={{
          expandedRowRender: (record) => (
            <BookingItemExpanded record={record} />
          ),
          rowExpandable: (record) =>
            !!(
              record.product_details ||
              record.event_details ||
              record.pricing
            ),
        }}
      />
    </div>
  );
}

export default BookingsTab;
